/* Maps a notification's semantic severity onto Stencil icons and design tokens. */
import IconAlertTriangleFillMedium from '@amzn/stencil-react-icons/icons/icon-alert-triangle-fill-medium';
import IconCheckCircleFillMedium from '@amzn/stencil-react-icons/icons/icon-check-circle-fill-medium';
import IconClockFillMedium from '@amzn/stencil-react-icons/icons/icon-clock-fill-medium';
import IconCloseCircleFillMedium from '@amzn/stencil-react-icons/icons/icon-close-circle-fill-medium';
import { NotificationSeverity } from './notificationPresentation';

export interface SeverityStencilPresentation {
  /** Leading icon component (24px medium fill icon). */
  Icon: typeof IconCheckCircleFillMedium;
  /** Icon color token carrying the outcome. */
  iconColor: string;
}

/**
 * One entry per severity: an icon shape AND a color, so the outcome is readable at a glance.
 *
 * Colors are the semantic status-icon tokens rather than raw palette values, which keeps them
 * meaningful (success / critical / info / warning) instead of an arbitrary set of hues, and lets
 * them follow the theme including dark mode. The shape still carries the same information on its
 * own, so the row remains readable for anyone who cannot distinguish the colors.
 */
const SEVERITY_PRESENTATION: Record<NotificationSeverity, SeverityStencilPresentation> = {
  success: { Icon: IconCheckCircleFillMedium, iconColor: 'color.status.success-icon' },
  critical: { Icon: IconCloseCircleFillMedium, iconColor: 'color.status.critical-icon' },
  // A clock reads as "waiting on someone" more directly than a person glyph does.
  pending: { Icon: IconClockFillMedium, iconColor: 'color.status.info-icon' },
  unknown: { Icon: IconAlertTriangleFillMedium, iconColor: 'color.status.warning-icon' }
};

/** Stencil icon and color token for a notification severity. */
export const getSeverityStencilPresentation = (severity: NotificationSeverity): SeverityStencilPresentation =>
  SEVERITY_PRESENTATION[severity];

/**
 * Width of the leading icon column.
 *
 * Fixed so every row's text starts on the same vertical line regardless of which icon
 * it carries. Stencil publishes spacing, border and z-index dimension tokens but no
 * sizing tokens, so a raw value is the sanctioned fallback for a width.
 */
export const ICON_COLUMN_WIDTH = '20px';
